import { observable, action, makeObservable, computed } from 'mobx';
import axios from 'axios';
import { ACCESS_TOKEN, appId } from '../../constants';
import { IClient } from './types';
import { getDefaultHeader } from '../../util/request';

export class UserModel {
  @observable.ref info: IClient | null = null;
  @observable loading = false;
  @observable error: string | null = null;
  @observable.ref purchasedList: string[] = [];

  @action
  private setUserInfo(user: IClient | null) {
    this.info = user;
  }

  @action
  private setPurchasedList(v: string[]) {
    this.purchasedList = v;
  }

  @action
  private setLoading(loading: boolean) {
    this.loading = loading;
  }

  @action
  private setError(error: string | null) {
    this.error = error;
  }

  async loginWithCode(code: string): Promise<boolean> {
    this.setLoading(true);
    try {
      const response = await axios.post('/api/client/login', { code, appId });
      localStorage.setItem(ACCESS_TOKEN, response.data.token);
      await this.fetchUserInfo();
      return true;
    } catch (error) {
      this.setError('登录失败');
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  async fetchUserInfo(): Promise<void> {
    this.setLoading(true);
    try {
      const response = await axios.get('/api/client/profile', {
        headers: { ...getDefaultHeader() },
      });
      this.setUserInfo(response.data);
    } catch (error) {
      this.setError('获取用户信息失败');
      this.handleTokenExpired(error);
    } finally {
      this.setLoading(false);
    }
    this.fetchUserPurchasedList();
  }

  async fetchUserPurchasedList(): Promise<void> {
    try {
      const response = await axios.get('/api/client/purchased-articles', {
        headers: { ...getDefaultHeader() },
      });
      this.setPurchasedList(response.data);
    } catch (error) {}
  }

  async fetchIsPurchased(
    id: string,
  ): Promise<{ purchased: boolean } | undefined> {
    try {
      const response = await axios.get(`/api/client/purchase/${id}`, {
        headers: { ...getDefaultHeader() },
      });
      return response.data;
    } catch (error) {}
  }

  private handleTokenExpired(error: any) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      this.logout();
      this.autoLogin();
    }
  }

  @action
  logout() {
    localStorage.removeItem(ACCESS_TOKEN);
    this.setUserInfo(null);
  }

  autoLogin() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      this.loginWithCode(code);
    } else {
      const redirectUri = encodeURIComponent(
        window.location.href.split('?')[0],
      );
      window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_base&state=123#wechat_redirect`;
    }
  }

  @computed
  get isBanned(): boolean {
    return this.info?.ban === 1;
  }

  async banUser(): Promise<boolean> {
    // if (!this.info) return false;

    this.setLoading(true);
    try {
      await axios.post(
        '/api/client/ban',
        {},
        {
          headers: { ...getDefaultHeader() },
        },
      );
      await this.fetchUserInfo(); // 刷新用户信息
      return true;
    } catch (error) {
      this.setError('封禁用户失败');
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  constructor() {
    makeObservable(this);
    // 初始化时检查token并自动登录
    const token = localStorage.getItem(ACCESS_TOKEN);
    if (!token) {
      this.autoLogin();
    } else {
      this.fetchUserInfo();
    }
  }
}

export default new UserModel();
